import { useEffect, useRef } from "react";

interface CelebrationProps {
  show: boolean;
  title: string;
  subtitle?: string;
  /** Libellé du bouton de fermeture (traduit côté appelant). */
  closeLabel: string;
  /** Appelé quand l'utilisateur ferme l'overlay. */
  onClose: () => void;
}

// Couleurs charte BizCoach pour les confettis.
const COLORS = ["#7CBF94", "#988EF2", "#F49390", "#F6C847", "#A6D3F9", "#384872"];

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vr: number;
  size: number;
  color: string;
  shape: 0 | 1; // rectangle | cercle
}

/**
 * Overlay de célébration plein écran : pluie de confettis (canvas, sans
 * dépendance) + message joyeux. Déclenché à la réussite d'une phase de parcours.
 */
export function Celebration({ show, title, subtitle, closeLabel, onClose }: CelebrationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!show) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width = Math.round(window.innerWidth * dpr);
      canvas.height = Math.round(window.innerHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const W = () => window.innerWidth;
    const H = () => window.innerHeight;

    // Salve initiale + réalimentation pendant ~2,5 s.
    const particles: Particle[] = [];
    const spawn = (n: number) => {
      for (let i = 0; i < n; i++) {
        particles.push({
          x: Math.random() * W(),
          y: -20 - Math.random() * H() * 0.3,
          vx: (Math.random() - 0.5) * 3,
          vy: 2 + Math.random() * 4,
          rot: Math.random() * Math.PI,
          vr: (Math.random() - 0.5) * 0.3,
          size: 6 + Math.random() * 8,
          color: COLORS[Math.floor(Math.random() * COLORS.length)]!,
          shape: Math.random() > 0.5 ? 0 : 1,
        });
      }
    };
    spawn(160);

    let frame = 0;
    const draw = () => {
      rafRef.current = requestAnimationFrame(draw);
      frame++;
      if (frame < 90 && frame % 8 === 0) spawn(24); // réalimentation ~1,5 s
      ctx.clearRect(0, 0, W(), H());
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i]!;
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.05; // gravité
        p.vx *= 0.99;
        p.rot += p.vr;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if (p.shape === 0) {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
        if (p.y > H() + 30) particles.splice(i, 1);
      }
    };
    draw();

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [show]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <canvas ref={canvasRef} className="pointer-events-none absolute inset-0" aria-hidden="true" />
      <div
        className="relative mx-4 max-w-md rounded-2xl bg-white px-8 py-7 text-center shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 text-5xl">🎉</div>
        <h2 className="text-2xl font-bold" style={{ color: "#1E7A3C", fontFamily: "Sora, sans-serif" }}>{title}</h2>
        {subtitle && <p className="mt-2 text-muted-foreground">{subtitle}</p>}
        <button
          onClick={onClose}
          className="mt-5 rounded-lg px-5 py-2 text-sm font-medium text-white"
          style={{ background: "#7CBF94" }}
        >
          🎊 {closeLabel}
        </button>
      </div>
    </div>
  );
}
